import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { PatientModel } from '../../../core/api/models/patient-model';
import { FormControl, Validators } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { PatientTargetModel } from '../../../core/api/models/patient-target-model';
import { SupervisorApiService } from '../../../core/api/services/supervisor-api.service';
import { AlertService } from '../../../shared/services/alert.service';
import { SupervisorHelperService } from '../../../supervisor/supervisor.helper.service';
import { AuthService } from '../../../auth/auth.service';
import { targetIsEchoic, targetIsPromptBased } from '../../../shared/utils/utils';
import { PatientTargetDataModel } from '../../../core/api/models/patient-target-data-model';
import { PromptCodeModel } from '../../../core/api/models/prompt-code-model';
import { SelectItem } from 'primeng/api';
import { Utils } from '../../../core/utils';
import { UserModel } from '../../../core/api/models/user-model';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { GraphApiService } from '../../../core/api/services/graph-api.service';
import { PatientSessionModel } from '../../../core/api/models/patient-session-model';
import { FluencyRateTimerMode, PatientTargetDataToileting, SessionSimpleDataModel, TargetType, DataTakerStaffDataModel } from '../../../core/api/models';
import { PatientBehaviorModel } from '../../../core/api/models/patient-behavior-model';
import { PatientBehaviorAntecedentModel, PatientBehaviorConsequenceModel, PatientBehaviorDataModel, PatientBehaviorLocationModel, PatientBehaviorResponseModel, PatientBehaviorSeverityModel, PatientBehaviorType } from '../../../core/api/models';
import { forkJoin } from 'rxjs';
import { Permissions } from "../../../core/api/models";
import { PatientDataFacadeService } from '../../../core/services/patient-data-facade.service';

@Component({
  selector: 'app-add-data',
  templateUrl: './add-data.component.html',
  styleUrl: './add-data.component.css',
  standalone: false,
})
export class AddDataComponent implements OnInit {

  @Input({ required: true }) selectedPatientID!: number;
  @Input({ required: false }) patientSession: PatientSessionModel | null = null;
  @Input({ required: false }) target: PatientTargetModel | null = null;
  @Input({ required: false }) behavior: PatientBehaviorModel | null = null;
  @Input({ required: false }) hideTargetSelector: boolean = false;
  @Input({ required: false }) hideBehaviorSelector: boolean = false;
  @Input({ required: false }) hideSessionSelector: boolean = false;
  @Output() close = new EventEmitter<boolean>();

  isLoading = signal(false);

  // Target Form
  addTargetForm = new FormGroup({
    recordedDate: new FormControl<string | null>(null, { validators: [Validators.required] }),
    target: new FormControl<PatientTargetModel | null>(null, { validators: [Validators.required] }),
    selectedPromptCode: new FormControl<PromptCodeModel | null>(null),
    selectedEchoicRate: new FormControl<string | null>(null),
    textValue: new FormControl<string | null>(null),
    duration: new FormControl<number | null>(null),
    happened: new FormControl<boolean>(false),
    incorrectCount: new FormControl<number | null>(null),
    correctCount: new FormControl<number | null>(null),
    selectedStaff: new FormControl<SelectItem<DataTakerStaffDataModel> | null>(null),
  });

  // Behavior Form
  addBehaviorForm = new FormGroup({
    recordedDate: new FormControl<string | null>(null, { validators: [Validators.required] }),
    behavior: new FormControl<PatientBehaviorModel | null>(null, { validators: [Validators.required] }),
    selectedAntecedent: new FormControl<PatientBehaviorAntecedentModel | null>(null),
    antecedentOther: new FormControl<string | null>(null),
    selectedSeverity: new FormControl<PatientBehaviorSeverityModel | null>(null),
    selectedResponse: new FormControl<PatientBehaviorResponseModel | null>(null),
    selectedLocation: new FormControl<PatientBehaviorLocationModel | null>(null),
    selectedConsequence: new FormControl<PatientBehaviorConsequenceModel | null>(null),
    consequenceOther: new FormControl<string | null>(null),
    textValue: new FormControl<string | null>(null),
    notes: new FormControl<string | null>(null),
    duration: new FormControl<number | null>(null),
    happenedDuringInterval: new FormControl<boolean>(false),
    selectedStaff: new FormControl<SelectItem<DataTakerStaffDataModel> | null>(null),
  });

  titleName: string = 'Add Data';
  actionName: string = 'Add';

  // Target related properties
  targets = signal<SelectItem[]>([]);
  selectedTarget = signal<PatientTargetModel | null>(null);
  targetStaffMembers = signal<DataTakerStaffDataModel[]>([]);
  targetStaffOptions = signal<SelectItem<DataTakerStaffDataModel>[]>([]);

  // Behavior related properties
  behaviors = signal<SelectItem[]>([]);
  behaviorStaffMembers = signal<DataTakerStaffDataModel[]>([]);
  behaviorStaffOptions = signal<SelectItem<DataTakerStaffDataModel>[]>([]);
  antecedents = signal<PatientBehaviorAntecedentModel[]>([]);
  consequences = signal<PatientBehaviorConsequenceModel[]>([]);
  severities = signal<PatientBehaviorSeverityModel[]>([]);
  responses = signal<PatientBehaviorResponseModel[]>([]);
  locations = signal<PatientBehaviorLocationModel[]>([]);

  selectedBehavior = signal<PatientBehaviorModel | null>(null);
  selectedAntecedents = signal<PatientBehaviorAntecedentModel[] | null>([]);
  selectedConsequences = signal<PatientBehaviorConsequenceModel[] | null>([]);
  selectedSeverity = signal<PatientBehaviorSeverityModel | null>(null);
  selectedResponse = signal<PatientBehaviorResponseModel | null>(null);
  selectedLocation = signal<PatientBehaviorLocationModel | null>(null);

  // Shared properties
  sessions = signal<SelectItem<SessionSimpleDataModel>[]>([]);
  selectedSession: SessionSimpleDataModel | null = null;

  // toileting
  urinationOn = signal<boolean>(false);
  urinationOff = signal<boolean>(false);
  bowelMovementOn = signal<boolean>(false);
  bowelMovementOff = signal<boolean>(false);
  noVoid = signal<boolean>(false);

  user: UserModel | null = null;

  creatingSession = signal<boolean>(false);
  newSessionStartDate: string | null = null;
  newSessionEndDate: string | null = null;

  activeTabIndex = 0; // 0: Target, 1: Behavior

  constructor(
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private graphApiService: GraphApiService,
    private alertService: AlertService,
    private authService: AuthService,
    private patientDataFacade: PatientDataFacadeService
  ) {

  }

  ngOnInit(): void {

    this.authService.user().subscribe((user) => {
      this.user = user;
    });

    if (this.target) {
      this.selectedTarget.set(this.target);
      this.addTargetForm.controls.target.setValue(this.target);
      this.defaultFluencyDuration();
    }

    if (this.behavior) {
      this.selectedBehavior.set(this.behavior);
      this.addBehaviorForm.controls.behavior.setValue(this.behavior);
    }

    if (this.patientSession) {
      this.hideSessionSelector = true;
      this.selectedSession = this.patientSession;
    }

    if (!this.hideSessionSelector) {
      this.loadSessions();
    }

    this.loadAllData();
  }

  loadAllData() {
    this.isLoading.set(true);

    const targetRequest = this.patientDataFacade.getTargets(this.selectedPatientID ?? 0, { onlyActive: false });
    const behaviorRequest = this.staffApiService.GetPatientBehaviors({ patientID: this.selectedPatientID ?? 0 });
    const antecedentRequest = this.staffApiService.GetPatientBehaviorAntecedents({ patientID: this.selectedPatientID ?? 0 });
    const consequenceRequest = this.staffApiService.GetPatientBehaviorConsequences({ patientID: this.selectedPatientID ?? 0 });
    const severityRequest = this.staffApiService.GetPatientBehaviorSeverities({});
    const responseRequest = this.staffApiService.GetPatientBehaviorResponses({});
    const locationRequest = this.staffApiService.GetPatientBehaviorLocations({});

    forkJoin({
      targets: targetRequest,
      behaviors: behaviorRequest,
      antecedents: antecedentRequest, 
      consequences: consequenceRequest,
      severities: severityRequest,
      responses: responseRequest,
      locations: locationRequest
    }).subscribe({
      next: (results) => {
        this.handleIncomingTargetsForAddData(results.targets);
        this.behaviors.set(results.behaviors.filter((b) => !b.IsDeleted && !b.IsArchived).map((b) => ({ 
          value: b, 
          label: this.displayNameForBehavior(b) 
        })));

        this.antecedents.set(results.antecedents);
        this.consequences.set(results.consequences);
        this.severities.set(results.severities);
        this.responses.set(results.responses);
        this.locations.set(results.locations);

        // Set initial recorded date from session start date if available, otherwise use current date
        const initialRecordedDate = this.getSessionStartDateForRecordedDate();

        this.addTargetForm.controls.recordedDate.setValue(initialRecordedDate);
        this.addTargetForm.controls.recordedDate.updateValueAndValidity();
        this.addBehaviorForm.controls.recordedDate.setValue(initialRecordedDate);
        this.addBehaviorForm.controls.recordedDate.updateValueAndValidity();

        // Load staff members if target or behavior is already selected
        if (this.selectedTarget()?.ID) {
          this.loadTargetStaffMembersForSelection();
        }
        if (this.selectedBehavior()?.ID) {
          this.loadBehaviorStaffMembersForSelection();
        }

        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  canSelectStaff() {
    return this.authService.userInfo?.Permissions?.includes(Permissions.SparkApp_Supervisor);
  }

  loadSessions() {
    this.isLoading.set(true);
    this.staffApiService.GetPatientSessionSimpleList({ patientID: this.selectedPatientID ?? 0 }).subscribe({
      next: (sessions) => {
        this.sessions.set(sessions.map((s) => ({ value: s, label: this.formattedDates(s.StartedDate, s.FinishedDate) + (s.ServiceID ? (' (Service: #' + s.ServiceID + ' - ' + (s.Staffs?.map(st => st.FullName ?? '').join(', ') ?? []) + ')') : '') })));
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  showCreateSession() {
    this.creatingSession.set(true);
    this.selectedSession = null;
  }

  hideCreateSession() {
    this.creatingSession.set(false);
    this.newSessionStartDate = null;
    this.newSessionEndDate = null;
  }

  formattedDates(startDate: string | null | undefined, endDate: string | null | undefined) {
    if (!startDate) {
      return 'Not started';
    }

    if (!endDate) {
      return new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    }

    //if on the same day, return the date and 2 times
    if (new Date(startDate).toDateString() === new Date(endDate).toDateString()) {
      let result = new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
      //add end time to the result
      result += ' - ' + new Date(endDate).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      return result;
    }

    //date in fomrat MM/DD/YYYY hh:mm AM/PM
    return new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) + ' - ' + new Date(endDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  }

  changedSession(event: any) {
    console.log(event);
    
    // Update selectedSession when session changes
    this.selectedSession = event.value;
    
    // Update recorded date when session changes
    if (event.value?.StartedDate) {
      const sessionStartDate = new Date(event.value.StartedDate);
      const newRecordedDate = new Date(sessionStartDate.getTime() - (sessionStartDate.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
      
      this.addTargetForm.controls.recordedDate.setValue(newRecordedDate);
      this.addTargetForm.controls.recordedDate.updateValueAndValidity();
      this.addBehaviorForm.controls.recordedDate.setValue(newRecordedDate);
      this.addBehaviorForm.controls.recordedDate.updateValueAndValidity();
    }
    
    // Reload staff members if target or behavior is already selected
    // This ensures staff from the selected session are included
    if (this.selectedTarget()) {
      this.loadTargetStaffMembersForSelection();
    }
    if (this.selectedBehavior()) {
      this.loadBehaviorStaffMembersForSelection();
    }
  }

  // Target related methods
  handleIncomingTargetsForAddData(targets: PatientTargetModel[]) {
    // filter out targets that are groupings of other targets
    const targetsWithSubtargets = targets.filter((t) => t.SubTargets && t.SubTargets.length > 0);

    let filteredTargets = targets.filter((t) => !targetsWithSubtargets.includes(t));

    // filter out deleted or archived targets
    filteredTargets = filteredTargets.filter((t) => !t.IsDeleted && !t.IsArchived);

    this.targets.set(filteredTargets.map((t) => ({ value: t, label: this.displayNameForTarget(t, targetsWithSubtargets) })));
  }

  displayNameForTarget(target: PatientTargetModel, targetsWithSubtargets: PatientTargetModel[]) {
    if (target.IsTargetSubtarget) {
      const parentTargets = targetsWithSubtargets.filter((t) => t.SubTargets?.some((st) => st.ID === target.ID));
      return target.TargetName + ' (' + Utils.patientTargetTypeLabel(target?.TargetType!) + ')' + ' - (grouped in target(s): ' + parentTargets.map((t) => t.TargetName).join(', ') + ')';
    } else {
      return target.TargetName + ' (' + Utils.patientTargetTypeLabel(target?.TargetType!) + ')';
    }
  }

  // Behavior related methods
  displayNameForBehavior(behavior: PatientBehaviorModel) {
    return behavior.PatientBehaviorName + ' (' + Utils.patientBehaviorTypeLabel(behavior?.BehaviorType!) + ')';
  }

  // Target value methods
  intValueForTarget() {
    if (this.targetIsPromptBased()) {
      return this.addTargetForm.controls.selectedPromptCode.value;
    } else if (this.targetIsFrequency()) {
      return 1;
    } else if (this.targetIsDuration() || this.targetIsToileting()) {
      return this.minutesToSeconds(this.addTargetForm.controls.duration.value!);
    } else if (this.targetIsWholePartialInterval()) {
      return this.addTargetForm.controls.happened.value ? 1 : 0;
    } else if (this.targetIsFluencyRate()) {
      return this.addTargetForm.controls.correctCount.value!;
    } else {
      return this.addTargetForm.controls.textValue.value;
    }
  }

  textValueForTarget() {
    if (this.targetIsPromptBased()) {
      const promptCode = this.selectedTarget()?.PromptCodes?.find((p) => p.ID === this.addTargetForm.controls.selectedPromptCode.value);
      if (this.targetIsEchoic()) {
        return promptCode?.Text! + this.addTargetForm.controls.selectedEchoicRate.value;
      } else {
        return promptCode?.Text;
      }
    } else if (this.targetIsIntValueOnly()) {
      return null;
    } else if (this.targetIsFrequency()) {
      return '1';
    } else if (this.targetIsDuration()) {
      const minutes = this.addTargetForm.controls.duration.value!;
      const seconds = this.minutesToSeconds(minutes);
      // format from seconds to hh:mm string
      const minutes1 = Math.floor((seconds % 3600) / 60);
      const seconds1 = seconds % 60;
      return `${minutes1 < 10 ? '0' + minutes1 : minutes1}:${seconds1 < 10 ? '0' + seconds1 : seconds1}`;
    } else if (this.targetIsToileting()) {
      return this.minutesToSeconds(this.addTargetForm.controls.duration.value!).toString();
    } else if (this.targetIsWholePartialInterval()) {
      return this.addTargetForm.controls.happened.value ? '1' : '0';
    } else if (this.targetIsFluencyRate()) {
      return this.addTargetForm.controls.correctCount.value! + '/' + this.addTargetForm.controls.incorrectCount.value!;
    } else {
      return this.addTargetForm.controls.textValue.value;
    }
  }

  // Behavior value methods
  intValueForBehavior() {
    if (this.behaviorIsFrequency()) {
      return 1;
    } else if (this.behaviorIsDuration()) {
      return this.minutesToSeconds(this.addBehaviorForm.controls.duration.value!);
    } else if (this.behaviorIsInterval()) {
      return this.addBehaviorForm.controls.happenedDuringInterval.value ? 1 : 0;
    } else {
      return this.addBehaviorForm.controls.textValue.value;
    }
  }

  textValueForBehavior() {
    if (this.behaviorIsFrequency()) {
      return '1';
    } else if (this.behaviorIsDuration()) {
      const minutes = this.addBehaviorForm.controls.duration.value!;
      const seconds = this.minutesToSeconds(minutes);
      // format from seconds to hh:mm string
      const minutes1 = Math.floor((seconds % 3600) / 60);
      const seconds1 = seconds % 60;
      return `${minutes1 < 10 ? '0' + minutes1 : minutes1}:${seconds1 < 10 ? '0' + seconds1 : seconds1}`;
    } else if (this.behaviorIsInterval()) {
      return this.addBehaviorForm.controls.happenedDuringInterval.value ? '1' : '0';
    } else {
      return this.addBehaviorForm.controls.textValue.value;
    }
  }

  // Target type checks
  targetIsIntValueOnly() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Quantity;
  }

  targetIsPromptBased() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return targetIsPromptBased(target);
  }

  targetIsEchoic() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return targetIsEchoic(target);
  }

  targetIsToileting() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Toileting;
  }

  targetIsFrequency() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Frequency;
  }

  targetIsDuration() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Duration;
  }

  targetIsWholePartialInterval() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.WholePartialInterval;
  }

  targetIsFluencyRate() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.FluencyRate;
  }

  targetIsOpenEndedFluencyRate() {
    return (
      this.targetIsFluencyRate() &&
      this.selectedTarget()?.FluencyRateTimerMode === FluencyRateTimerMode.OpenEnded
    );
  }

  defaultFluencyDuration() {
    const target = this.selectedTarget();
    if (target?.TargetType !== TargetType.FluencyRate) {
      return;
    }

    const durationInMinutes = target.FluencyRateTimerMode === FluencyRateTimerMode.OpenEnded
      ? null
      : this.secondsToMinutes(target.Duration ?? 0);
    this.addTargetForm.controls.duration.setValue(durationInMinutes);
  }

  durationForTargetData() {
    if (this.targetIsFluencyRate()) {
      return this.minutesToSeconds(this.addTargetForm.controls.duration.value!);
    }

    return undefined;
  }

  // Behavior type checks
  behaviorIsFrequency() {
    const behavior = this.selectedBehavior();
    if (!behavior) {
      return false;
    }
    return behavior?.BehaviorType === PatientBehaviorType.Frequency;
  }

  behaviorIsDuration() {
    const behavior = this.selectedBehavior();
    if (!behavior) {
      return false;
    }
    return behavior?.BehaviorType === PatientBehaviorType.Duration;
  }

  behaviorIsInterval() {
    const behavior = this.selectedBehavior();
    if (!behavior) {
      return false;
    }
    return behavior?.BehaviorType === PatientBehaviorType.Interval;
  }

  // Target methods
  promptOptionsForTarget() {
    const target = this.selectedTarget();
    if (!target) {
      return [];
    }
    const promptOptionsForTarget = target?.PromptCodes?.map((p) => {
      return {
        value: p.ID,
        text: p.Text,
      };
    });
    return promptOptionsForTarget;
  }

  extraOptionsForEchoicTarget() {
    const extraOptionsForEchoicTarget = [
      {
        value: '|Articulation',
        text: 'Articulation',
      },
      {
        value: '|Volume',
        text: 'Volume',
      },
      {
        value: '|Speed',
        text: 'Speed',
      },
      {
        value: '|Tone',
        text: 'Tone',
      },
      {
        value: '|Rhythm',
        text: 'Rhythm',
      },
    ];
    return extraOptionsForEchoicTarget;
  }

  // Submit methods
  onSubmitTarget() {
    console.log(this.addTargetForm.value);

    // validate form
    if (this.addTargetForm.invalid) {
      this.alertService.error('Data is not valid, please check the error messages and try again.');
      return;
    }

    const recordedDateToSend = this.addTargetForm.controls.recordedDate.value ?
      this.addTargetForm.controls.recordedDate.value :
      new Date().toServerString();

    if (!this.patientSession && !this.selectedSession && !this.creatingSession()) {
      this.alertService.error('Session is required');
      return;
    }

    if (this.creatingSession() && (!this.newSessionStartDate || !this.newSessionEndDate)) {
      this.alertService.error('Session dates are required');
      return;
    }

    if (this.targetIsFluencyRate() && !this.fluencyRateDataIsValid()) {
      this.alertService.error('Fluency Rate data requires correct count, incorrect count, and duration.');
      return;
    }

    if ((this.intValueForTarget() === null || this.intValueForTarget() === undefined)  && !this.textValueForTarget() ) {
      this.alertService.error('Target data is not valid, please add some value to the target.');
      return;
    }

    const sessionIDToSend = this.patientSession ? this.patientSession?.ID : this.selectedSession?.ID;

    // Get selected staff or use current user
    const selectedStaffValue = this.addTargetForm.controls.selectedStaff.value;
    const staffID = selectedStaffValue?.value?.StaffID ?? this.user?.ID ?? 0;
    const staffFullName = selectedStaffValue?.value?.FullName ?? this.user?.Fullnane ?? '';
    const currentUser = this.user;
    const currentUserID = currentUser?.ID ?? 0;
    
    // build target data to be saved
    const targetData: PatientTargetDataModel = {
      PatientSessionID: sessionIDToSend,
      PatientTargetID: this.selectedTarget()?.ID ?? 0,
      RecordedDate: recordedDateToSend,
      IntValue: Number(this.intValueForTarget()) ?? null,
      TextValue: this.textValueForTarget() ?? null,
      Duration: this.durationForTargetData(),
      UserID: staffID, // Use selected staff from dropdown, or current user
      UserFullName: staffFullName,
    }
    
    // Note: ModifiedByUserID, ModifiedDate, ManuallyAddedByUserID, and ManuallyAddedDate 
    // are all set by the backend and should not be set here

    if (this.targetIsToileting()) {
      const toiletingData: PatientTargetDataToileting = {
        UrinationOn: this.urinationOn(),
        UrinationOff: this.urinationOff(),
        BowelMovementOn: this.bowelMovementOn(),
        BowelMovementOff: this.bowelMovementOff(),
        NoVoid: this.noVoid(),
        Duration: this.minutesToSeconds(this.addTargetForm.controls.duration.value!),
      }
      targetData.ToiletingData = toiletingData;
    }

    console.log(targetData);

    // append new target data to patient session
    this.saveTargetData(targetData);
  }

  onSubmitBehavior() {
    console.log(this.addBehaviorForm.value);

    // validate form
    if (this.addBehaviorForm.invalid) {
      this.alertService.error('Data is not valid, please check the error messages and try again.');
      return;
    }

    const recordedDateToSend = this.addBehaviorForm.controls.recordedDate.value ?
      this.addBehaviorForm.controls.recordedDate.value :
      new Date().toServerString();

    if (!this.patientSession && !this.selectedSession && !this.creatingSession()) {
      this.alertService.error('Session is required');
      return;
    }

    if (this.creatingSession() && (!this.newSessionStartDate || !this.newSessionEndDate)) {
      this.alertService.error('Session dates are required');
      return;
    }

    if ((this.intValueForBehavior() === null || this.intValueForBehavior() === undefined) && !this.textValueForBehavior()) {
      this.alertService.error('Behavior data is not valid, please add some value to the behavior.');
      return;
    }

    const sessionIDToSend = this.patientSession ? this.patientSession?.ID : this.selectedSession?.ID;

    // Get selected staff or use current user
    const selectedStaffValue = this.addBehaviorForm.controls.selectedStaff.value;
    const staffID = selectedStaffValue?.value?.StaffID ?? this.user?.ID ?? 0;
    const staffFullName = selectedStaffValue?.value?.FullName ?? this.user?.Fullnane ?? '';
    const currentUser = this.user;
    const currentUserID = currentUser?.ID ?? 0;
    
    // build behavior data to be saved
    const behaviorData: PatientBehaviorDataModel = {
      PatientSessionID: sessionIDToSend,
      PatientBehaviorID: this.selectedBehavior()?.ID ?? 0,
      RecordedDate: recordedDateToSend,
      IntValue: Number(this.intValueForBehavior()) ?? null,
      TextValue: this.textValueForBehavior() ?? null,
      UserID: staffID, // Use selected staff from dropdown, or current user
      UserFullName: staffFullName,
      PatientBehaviorAntecedentIDs: this.addBehaviorForm.controls.selectedAntecedent.value?.ID ? [this.addBehaviorForm.controls.selectedAntecedent.value?.ID] : null,
      PatientBehaviorConsequenceIDs: this.addBehaviorForm.controls.selectedConsequence.value?.ID ? [this.addBehaviorForm.controls.selectedConsequence.value?.ID] : null,
      PatientBehaviorSeverityID: this.addBehaviorForm.controls.selectedSeverity.value?.ID ?? null,
      PatientBehaviorResponseID: this.addBehaviorForm.controls.selectedResponse.value?.ID ?? null,
      PatientBehaviorLocationID: this.addBehaviorForm.controls.selectedLocation.value?.ID ?? null,
      Notes: this.addBehaviorForm.controls.notes.value ?? null,
    }
    
    // Note: ModifiedByUserID, ModifiedDate, ManuallyAddedByUserID, and ManuallyAddedDate 
    // are all set by the backend and should not be set here

    console.log(behaviorData);

    // append new behavior data to patient session
    this.saveBehaviorData(behaviorData);
  }

  // Save methods
  saveTargetData(targetData: PatientTargetDataModel) {
    this.isLoading.set(true);
    console.log('Saving target data:', targetData);

    if (this.creatingSession()) {
      this.staffApiService.SavePatientSession({
        body: {
          PatientID: this.selectedPatientID,
          StartedDate: this.newSessionStartDate!,
          FinishedDate: this.newSessionEndDate!,
        },
      }).subscribe(
        {
          next: (response) => {
            console.log('Created session:', response);
            targetData.PatientSessionID = response.ID;
            this.doSaveTargetData(targetData);
          },
          error: (error) => {
            console.error('Error creating session:', error);
            this.alertService.error('Error creating session - ' + error.Message);
            this.isLoading.set(false);
          }
        }
      );
    } else {
      this.doSaveTargetData(targetData);
    }
  }

  doSaveTargetData(targetData: PatientTargetDataModel) {
    this.isLoading.set(true);
    
    // UserID should already be set (from dropdown or current user)
    // If not set, use current user as fallback
    const currentUser = this.user;
    const currentUserID = currentUser?.ID;
    if (!targetData.UserID && currentUserID) {
      targetData.UserID = currentUserID;
      targetData.UserFullName = currentUser?.Fullnane ?? '';
    }
    
    // Set ManuallyAddedByUserID when adding data manually to an existing session from sessions list
    // (not in active session and not creating a new session)
    if (!this.patientSession && this.selectedSession && !this.creatingSession() && currentUserID) {
      targetData.ManuallyAddedByUserID = currentUserID;
      targetData.ManuallyAddedDate = new Date().toISOString();
    }
    
    this.doSaveTargetDataWithAPI(targetData);
  }

  private doSaveTargetDataWithAPI(targetData: PatientTargetDataModel) {
    this.staffApiService
      .SavePatientTargetData({
        body: targetData!,
        addedManually: true,
      })
      .subscribe(
        {
          next: (response) => {
            console.log('Saved target data:', response);
            this.alertService.success('Target data saved successfully');
            this.isLoading.set(false);
            this.close.emit(true);
          },
          error: (error) => {
            console.error('Error saving target data:', error);
            this.alertService.error(
              'Error saving target data - ' + error.Message
            );
            this.isLoading.set(false);
          }
        }
      );
  }

  saveBehaviorData(behaviorData: PatientBehaviorDataModel) {
    this.isLoading.set(true);
    console.log('Saving behavior data:', behaviorData);

    if (this.creatingSession()) {
      this.staffApiService.SavePatientSession({
        body: {
          PatientID: this.selectedPatientID,
          StartedDate: this.newSessionStartDate!,
          FinishedDate: this.newSessionEndDate!,
        },
      }).subscribe(
        {
          next: (response) => {
            console.log('Created session:', response);
            behaviorData.PatientSessionID = response.ID;
            this.doSaveBehaviorData(behaviorData);
          },
          error: (error) => {
            console.error('Error creating session:', error);
            this.alertService.error('Error creating session - ' + error.Message);
            this.isLoading.set(false);
          }
        }
      );
    } else {
      this.doSaveBehaviorData(behaviorData);
    }
  }

  doSaveBehaviorData(behaviorData: PatientBehaviorDataModel) {
    this.isLoading.set(true);
    
    // UserID should already be set (from dropdown or current user)
    // If not set, use current user as fallback
    const currentUser = this.user;
    const currentUserID = currentUser?.ID;
    if (!behaviorData.UserID && currentUserID) {
      behaviorData.UserID = currentUserID;
      behaviorData.UserFullName = currentUser?.Fullnane ?? '';
    }
    
    // Set ManuallyAddedByUserID when adding data manually to an existing session from sessions list
    // (not in active session and not creating a new session)
    if (!this.patientSession && this.selectedSession && !this.creatingSession() && currentUserID) {
      behaviorData.ManuallyAddedByUserID = currentUserID;
      behaviorData.ManuallyAddedDate = new Date().toISOString();
    }
    
    // Note: ModifiedByUserID and ModifiedDate are set by the backend
    
    this.doSaveBehaviorDataWithAPI(behaviorData);
  }

  private doSaveBehaviorDataWithAPI(behaviorData: PatientBehaviorDataModel) {
    this.staffApiService
      .SavePatientBehaviorData({
        body: behaviorData!,
        addedManually: true,
      })
      .subscribe(
        {
          next: (response) => {
            console.log('Saved behavior data:', response);
            this.alertService.success('Behavior data saved successfully');
            this.isLoading.set(false);
            this.close.emit(true);
          },
          error: (error) => {
            console.error('Error saving behavior data:', error);
            this.alertService.error(
              'Error saving behavior data - ' + error.Message
            );
            this.isLoading.set(false);
          }
        }
      );
  }

  // Utility methods
  minutesToSeconds(minutes: number): number {
    return Math.round(minutes * 60);
  }

  secondsToMinutes(seconds: number): number {
    return seconds / 60;
  }

  onCancel() {
    this.close.emit(true);
  }

  changedTarget(event: any) {
    if (event.value) {
      this.selectedTarget.set(event.value.value);
      this.defaultFluencyDuration();
      this.loadTargetStaffMembersForSelection();
    }
  }

  private fluencyRateDataIsValid() {
    const correctCount = this.addTargetForm.controls.correctCount.value;
    const incorrectCount = this.addTargetForm.controls.incorrectCount.value;
    const duration = this.addTargetForm.controls.duration.value;

    return (
      correctCount !== null &&
      correctCount !== undefined &&
      incorrectCount !== null &&
      incorrectCount !== undefined &&
      duration !== null &&
      duration !== undefined &&
      duration > 0
    );
  }
  
  changedBehavior(event: any) {
    if (event.value) {
      this.selectedBehavior.set(event.value.value);
      this.loadBehaviorStaffMembersForSelection();
    }
  }

  // Tab change handler (optional, for future use)
  onTabChange(event: any) {
    this.activeTabIndex = event.index;
  }

  // Custom tab switching method
  setActiveTab(index: number) {
    this.activeTabIndex = index;
  }
  
  loadTargetStaffMembersForSelection() {
    let selectedSessionValue: PatientSessionModel | null = this.selectedSession;

    // if no session is selected, just return;
    if (!selectedSessionValue) {
      return;
    } 
    
    if (!selectedSessionValue.ServiceID) {
      // if session has no service, just use the current user
      const currentUserStaff: DataTakerStaffDataModel = {
        StaffID: this.user?.ID ?? 0,
        FullName: this.user?.Fullnane ?? '',
        Email: null
      };
      this.targetStaffMembers.set([currentUserStaff]);
      this.updateTargetStaffOptions();
      
      // Preselect current user
      this.preselectTargetCurrentUser();
    } else if (selectedSessionValue.StaffIDs && selectedSessionValue.StaffIDs.length > 0) {
        // if session has a service, get the staff from the session(which came from the service)
        // just get the first one for now
        const staffID = selectedSessionValue.StaffIDs[0];
        const staffFullName = selectedSessionValue.StaffNames?.[0] ?? '';
        const staff: DataTakerStaffDataModel = {
          StaffID: staffID,
          FullName: staffFullName,
          Email: null
        };
        this.targetStaffMembers.set([staff]);
        this.updateTargetStaffOptions();
        
        // preselect staff from session
        this.preselectFirstTargetStaffFromSession([staff]);
    }
  }

  preselectTargetCurrentUser() {
    if (this.user && this.user.ID) {
      const currentUserOption = this.targetStaffOptions().find(s => s.value?.StaffID === this.user!.ID);
      if (currentUserOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: currentUserOption.label,
          value: currentUserOption.value
        };
        this.addTargetForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }

  preselectFirstTargetStaffFromSession(staff: DataTakerStaffDataModel[]) {
    if (staff && staff.length > 0) {
      const staffOption = this.targetStaffOptions().find(s => s.value?.StaffID === staff[0].StaffID);
      if (staffOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: staffOption.label,
          value: staffOption.value
        };
        this.addTargetForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }
  
  updateTargetStaffOptions() {
    this.targetStaffOptions.set(
      this.targetStaffMembers().map(staff => ({
        label: staff.FullName ?? '',
        value: staff
      }))
    );
  }

  loadBehaviorStaffMembersForSelection() {
    let selectedSessionValue: PatientSessionModel | null = this.selectedSession;

    // if no session is selected, just return;
    if (!selectedSessionValue) {
      return;
    } 
    
    if (!selectedSessionValue.ServiceID) {
      // if session has no service, just use the current user
      const currentUserStaff: DataTakerStaffDataModel = {
        StaffID: this.user?.ID ?? 0,
        FullName: this.user?.Fullnane ?? '',
        Email: null
      };
      this.behaviorStaffMembers.set([currentUserStaff]);
      this.updateBehaviorStaffOptions();
      
      // Preselect current user
      this.preselectBehaviorCurrentUser();
    } else if (selectedSessionValue.StaffIDs && selectedSessionValue.StaffIDs.length > 0) {
        // just get the first one for now
        const staffID = selectedSessionValue.StaffIDs[0];
        const staffFullName = selectedSessionValue.StaffNames?.[0] ?? '';
        const staff: DataTakerStaffDataModel = {
          StaffID: staffID,
          FullName: staffFullName,
          Email: null
        };
        this.behaviorStaffMembers.set([staff]);
        this.updateBehaviorStaffOptions();
        
        // preselect staff from session
        this.preselectFirstBehaviorStaffFromSession([staff]);
    }
  }

  preselectBehaviorCurrentUser() {
    if (this.user && this.user.ID) {
      const currentUserOption = this.behaviorStaffOptions().find(s => s.value?.StaffID === this.user!.ID);
      if (currentUserOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: currentUserOption.label,
          value: currentUserOption.value
        };
        this.addBehaviorForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }

  preselectFirstBehaviorStaffFromSession(staff: DataTakerStaffDataModel[]) {
    if (staff && staff.length > 0) {
      const staffOption = this.behaviorStaffOptions().find(s => s.value?.StaffID === staff[0].StaffID);
      if (staffOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: staffOption.label,
          value: staffOption.value
        };
        this.addBehaviorForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }
  
  updateBehaviorStaffOptions() {
    this.behaviorStaffOptions.set(
      this.behaviorStaffMembers().map(staff => ({
        label: staff.FullName ?? '',
        value: staff
      }))
    );
  }

  // Helper method to get session start date for recorded date
  private getSessionStartDateForRecordedDate(): string {
    if (this.patientSession?.StartedDate) {
      const sessionStartDate = new Date(this.patientSession.StartedDate);
      return new Date(sessionStartDate.getTime() - (sessionStartDate.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
    } else if (this.selectedSession?.StartedDate) {
      const sessionStartDate = new Date(this.selectedSession.StartedDate);
      return new Date(sessionStartDate.getTime() - (sessionStartDate.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
    } else {
      // Fallback to current date if no session is available
      const now = new Date();
      return new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
    }
  }

  // Save and Add More for Target
  onSaveAndAddMoreTarget() {
    if (this.addTargetForm.invalid) {
      this.alertService.error('Data is not valid, please check the error messages and try again.');
      return;
    }
    const recordedDateToSend = this.addTargetForm.controls.recordedDate.value ?
      this.addTargetForm.controls.recordedDate.value :
      new Date().toServerString();
    if (!this.patientSession && !this.selectedSession && !this.creatingSession()) {
      this.alertService.error('Session is required');
      return;
    }
    if (this.creatingSession() && (!this.newSessionStartDate || !this.newSessionEndDate)) {
      this.alertService.error('Session dates are required');
      return;
    }
    if ((this.intValueForTarget() === null || this.intValueForTarget() === undefined)  && !this.textValueForTarget() ) {
      this.alertService.error('Target data is not valid, please add some value to the target.');
      return;
    }
    const sessionIDToSend = this.patientSession ? this.patientSession?.ID : this.selectedSession?.ID;
    const currentUser = this.user;
    const currentUserID = currentUser?.ID ?? 0;
    
    const targetData: PatientTargetDataModel = {
      PatientSessionID: sessionIDToSend,
      PatientTargetID: this.selectedTarget()?.ID ?? 0,
      RecordedDate: recordedDateToSend,
      IntValue: Number(this.intValueForTarget()) ?? null,
      TextValue: this.textValueForTarget() ?? null,
      UserID: currentUserID, // Will be set from dropdown if available, otherwise current user
      UserFullName: currentUser?.Fullnane ?? '',
    }
    
    // Note: ModifiedByUserID, ModifiedDate, ManuallyAddedByUserID, and ManuallyAddedDate 
    // are all set by the backend and should not be set here
    
    if (this.targetIsToileting()) {
      const toiletingData: PatientTargetDataToileting = {
        UrinationOn: this.urinationOn(),
        UrinationOff: this.urinationOff(),
        BowelMovementOn: this.bowelMovementOn(),
        BowelMovementOff: this.bowelMovementOff(),
        NoVoid: this.noVoid(),
        Duration: this.minutesToSeconds(this.addTargetForm.controls.duration.value!),
      }
      targetData.ToiletingData = toiletingData;
    }
    this.isLoading.set(true);
    this.staffApiService.SavePatientTargetData({ body: targetData!, addedManually: true }).subscribe({
      next: (response) => {
        this.alertService.success('Target data saved successfully');
        this.isLoading.set(false);
        this.addTargetForm.reset();
        
        // Reset recorded date to session start date after form reset
        const sessionStartDate = this.getSessionStartDateForRecordedDate();
        this.addTargetForm.controls.recordedDate.setValue(sessionStartDate);
        this.addTargetForm.controls.recordedDate.updateValueAndValidity();
        
        // Optionally reset other signals/fields if needed
      },
      error: (error) => {
        this.alertService.error('Error saving target data - ' + error.Message);
        this.isLoading.set(false);
      }
    });
  }

  // Save and Add More for Behavior
  onSaveAndAddMoreBehavior() {
    if (this.addBehaviorForm.invalid) {
      this.alertService.error('Data is not valid, please check the error messages and try again.');
      return;
    }
    const recordedDateToSend = this.addBehaviorForm.controls.recordedDate.value ?
      this.addBehaviorForm.controls.recordedDate.value :
      new Date().toServerString();
    if (!this.patientSession && !this.selectedSession && !this.creatingSession()) {
      this.alertService.error('Session is required');
      return;
    }
    if (this.creatingSession() && (!this.newSessionStartDate || !this.newSessionEndDate)) {
      this.alertService.error('Session dates are required');
      return;
    }
    if ((this.intValueForBehavior() === null || this.intValueForBehavior() === undefined) && !this.textValueForBehavior()) {
      this.alertService.error('Behavior data is not valid, please add some value to the behavior.');
      return;
    }
    const sessionIDToSend = this.patientSession ? this.patientSession?.ID : this.selectedSession?.ID;
    const currentUser = this.user;
    const currentUserID = currentUser?.ID ?? 0;
    
    const behaviorData: PatientBehaviorDataModel = {
      PatientSessionID: sessionIDToSend,
      PatientBehaviorID: this.selectedBehavior()?.ID ?? 0,
      RecordedDate: recordedDateToSend,
      IntValue: Number(this.intValueForBehavior()) ?? null,
      TextValue: this.textValueForBehavior() ?? null,
      UserID: currentUserID, // Will be set from dropdown if available, otherwise current user
      UserFullName: currentUser?.Fullnane ?? '',
      PatientBehaviorAntecedentIDs: this.addBehaviorForm.controls.selectedAntecedent.value?.ID ? [this.addBehaviorForm.controls.selectedAntecedent.value?.ID] : null,
      PatientBehaviorConsequenceIDs: this.addBehaviorForm.controls.selectedConsequence.value?.ID ? [this.addBehaviorForm.controls.selectedConsequence.value?.ID] : null,
      PatientBehaviorSeverityID: this.addBehaviorForm.controls.selectedSeverity.value?.ID ?? null,
      PatientBehaviorResponseID: this.addBehaviorForm.controls.selectedResponse.value?.ID ?? null,
      PatientBehaviorLocationID: this.addBehaviorForm.controls.selectedLocation.value?.ID ?? null,
      Notes: this.addBehaviorForm.controls.notes.value ?? null,
    }
    
    // Note: ModifiedByUserID, ModifiedDate, ManuallyAddedByUserID, and ManuallyAddedDate 
    // are all set by the backend and should not be set here
    
    this.isLoading.set(true);
    this.staffApiService.SavePatientBehaviorData({ body: behaviorData!, addedManually: true }).subscribe({
      next: (response) => {
        this.alertService.success('Behavior data saved successfully');
        this.isLoading.set(false);
        this.addBehaviorForm.reset();
        
        // Reset recorded date to session start date after form reset
        const sessionStartDate = this.getSessionStartDateForRecordedDate();
        this.addBehaviorForm.controls.recordedDate.setValue(sessionStartDate);
        this.addBehaviorForm.controls.recordedDate.updateValueAndValidity();
        
        // Optionally reset other signals/fields if needed
      },
      error: (error) => {
        this.alertService.error('Error saving behavior data - ' + error.Message);
        this.isLoading.set(false);
      }
    });
  }

  selectedSessionLabel(session: PatientSessionModel | null) {
    if (session) {
      return 'Session: ' + this.formattedDates(session.StartedDate, session.FinishedDate) + (session.ServiceID ? (' (Service: #' + session.ServiceID + ' - ' + session.StaffName) : '' ) + ')';
    } else {
      return '';
    }
  }

  truncateMiddle(value: string | null | undefined, maxLength: number = 90): string {
    if (!value) {
      return '';
    }

    if (value.length <= maxLength) {
      return value;
    }

    const separator = '...';
    const available = maxLength - separator.length;
    const startLength = Math.ceil(available / 2);
    const endLength = Math.floor(available / 2);

    return `${value.slice(0, startLength)}${separator}${value.slice(value.length - endLength)}`;
  }

  shouldShowTargetStaffSelector() {
    return !this.hideTargetSelector && this.selectedTarget() !== null && this.selectedTarget() !== undefined && !this.creatingSession();
  }

  shouldShowBehaviorStaffSelector() {
    return !this.hideBehaviorSelector && this.selectedBehavior() !== null && this.selectedBehavior() !== undefined && !this.creatingSession();
  }
}
